import { Children, isValidElement, useState, type HTMLAttributes, type ReactElement } from 'react';
import { usePhone } from '@/lib/responsive';
import { Icons } from './primitives';
import { ResponsivePicker } from './responsive-picker';

type PhonePlacement = 'inline' | 'meta' | 'overflow' | 'hidden';

/**
 * The action row of a page header. On desktop it is exactly the
 * `<div className="page-actions">` it replaces. On phones each child's
 * `data-phone` decides its place: `inline` stays on the title row (the one
 * primary action, an icon-only Export), `meta` takes the full-width line
 * under it (the freshness chip, a count), `hidden` is dropped, and the rest —
 * the default — wait behind a ⋯ button that opens them as a sheet.
 *
 * On phone the wrapper is `display: contents` (`responsive.css`), so the
 * title, the status pill, the inline actions, the ⋯ and the meta line are all
 * flex items of `.page-hd` and the header keeps to one row of controls.
 */
export function PageActions({ children, className, ...props }: HTMLAttributes<HTMLDivElement>) {
  const phone = usePhone();
  const [open, setOpen] = useState(false);
  const classes = className ?? 'page-actions';
  if (!phone) {
    return (
      <div {...props} className={classes}>
        {children}
      </div>
    );
  }
  const items = Children.toArray(children).filter(isValidElement) as ReactElement<{
    'data-phone'?: PhonePlacement;
  }>[];
  const placement = (el: (typeof items)[number]): PhonePlacement => el.props['data-phone'] ?? 'overflow';
  const inline = items.filter((el) => placement(el) === 'inline');
  const meta = items.filter((el) => placement(el) === 'meta');
  const overflow = items.filter((el) => placement(el) === 'overflow');
  return (
    <div {...props} className={`${classes} page-actions--phone`}>
      {inline}
      {overflow.length > 0 && (
        <button
          type="button"
          className="btn page-actions-more"
          aria-label="More actions"
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen(true)}
        >
          {Icons.more}
        </button>
      )}
      {meta.length > 0 && <div className="page-actions-meta">{meta}</div>}
      {open && (
        <ResponsivePicker label="More actions" onClose={() => setOpen(false)}>
          <div
            className="page-actions-sheet"
            onClick={(event) => {
              // An action that completes on the tap closes the sheet; a control
              // that opens its own picker (a date range) keeps it.
              if ((event.target as HTMLElement).closest('button:not(.dd-trigger), a')) setOpen(false);
            }}
          >
            {overflow}
          </div>
        </ResponsivePicker>
      )}
    </div>
  );
}
